import { Common } from './common.model';
import {
    Embedded,
    EmbeddedUser,
    toUserEmbedded,
} from './embed.model';

export interface Vigilance {
    // The reason for placing the company under vigilance.
    // This should match a predefined list of possible reasons.
    reason: string;
    // The date when the vigilance status ends.
    endDate: string;
}

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $salesManager: EmbeddedUser;
}

export type EmbedCompany<T extends embed> = Company & Partial<Pick<EmbedProperties, T>>;

export const configuration: { [K in embed]: { embed: string; transform: (company: Company, embedded: Embedded) => EmbedProperties[K] } } = {
    $createdBy: {
        embed: 'users',
        transform: (data: Company, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Company, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: Company, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $salesManager: {
        embed: 'users',
        transform: (data: Company, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'salesManager', embedded),
    },
};

export class Company extends Common {
    declare public id: string;
    public name: string;
    public isCustomer: boolean;
    public salesManager: string;
    public source: string;
    public status: companyStatusEnum;
    public billingId: string;
    public universalId: string;
    public corporateName: string;
    public businessUnit?: string;
    public vertical?: string;
    public tiers?: string;
    public vigilance?: Vigilance;

    constructor(data: any) {
        super(data);
        this.id = data.id;
        this.name = data.name;
        this.isCustomer = data.isCustomer;
        this.salesManager = data.salesManager;
        this.source = data.source;
        this.status = data.status;
        this.billingId = data.billingId;
        this.universalId = data.universalId;
        this.corporateName = data.corporateName;
        this.businessUnit = data?.businessUnit;
        this.vertical = data?.vertical;
        this.tiers = data?.tiers;
        this.vigilance = data?.vigilance;
    }

    public static toCompanyEmbedded<T extends embed>(data: any, embedded?: Embedded, customFields?: T[]): EmbedCompany<T> {
        if (!embedded || !customFields) {
            return new Company(data) as EmbedCompany<T>;
        }
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});

        // TODO
        // hacky
        // metadata has to be convert to map...
        return { ...new Company(data), ...customData } as EmbedCompany<T>;
    }

    public static toCompanyEmbeddedList<T extends embed>(data: any[], embedded: Embedded, customFields: T[] = []): EmbedCompany<T>[] {
        return data.map((company) => this.toCompanyEmbedded(company, embedded, customFields));
    }
}

export enum companyStatusEnum {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE'
}
